import './TournamentPrizeModal.scss';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { tournamentApiService } from '../../../../../platform/api/tournamentApiService';

// Extend Window interface for global function
declare global {
  interface Window {
    checkForTournamentPrizes?: () => Promise<void>;
  }
}

interface TournamentPrize {
  tournamentId: number;
  placement: number;
  prizeAmount: number;
  prizeType: 'cash' | 'bonus' | 'free_spins';
  currency: string;
  claimed: boolean;
  tournamentName: string;
  tournamentBanner: string;
}

export const TournamentPrizeModal = observer(() => {
  const [isClosing, setIsClosing] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [prizeData, setPrizeData] = useState<TournamentPrize | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Function to check for prizes (can be called externally)
  const checkForPrizes = async () => {
    try {
      setIsLoading(true);
      const unseenNotifications = await tournamentApiService.getUnseenParticipantNotifications();

      if (unseenNotifications && unseenNotifications.length > 0) {
        // For now, we'll show the first notification
        // In the future, you might want to show multiple or queue them
        const notification = unseenNotifications[0];

        // Fetch tournament details to get name and banner
        const tournament = await tournamentApiService.getTournamentById(notification.tournamentId);

        if (tournament) {
          // Map IParticipant to TournamentPrize format with actual tournament data
          const prizeData: TournamentPrize = {
            tournamentId: notification.tournamentId,
            placement: notification.placement,
            prizeAmount: notification.winAmount || 0,
            prizeType: 'cash', // Default to cash, adjust based on your business logic
            currency: notification.currency,
            claimed: false,
            tournamentName: tournament.name,
            tournamentBanner: tournament.banner,
          };

          setPrizeData(prizeData);
          setIsVisible(true);
        } else {
          console.error('Failed to fetch tournament details for notification:', notification.tournamentId);
        }
      } else {
        // No unseen notifications, don't show the modal
        setIsVisible(false);
      }
    } catch (error) {
      console.error('Error fetching unseen notifications:', error);
      // Don't show the modal on error
      setIsVisible(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Check on component mount
    checkForPrizes();

    // Set up periodic check for new notifications (every 30 seconds)
    // const intervalId = setInterval(checkForPrizes, 30000);

    // Listen for new prize notifications (could be from WebSocket or other events)
    const handlePrizeNotification = (event: CustomEvent) => {
      const newPrize: TournamentPrize = event.detail;
      setPrizeData(newPrize);
      setIsVisible(true);
    };

    window.addEventListener('tournamentPrizeWon', handlePrizeNotification as EventListener);

    // Expose the checkForPrizes function globally for external use
    window.checkForTournamentPrizes = checkForPrizes;

    return () => {
      // clearInterval(intervalId);
      window.removeEventListener('tournamentPrizeWon', handlePrizeNotification as EventListener);
      // Clean up global function
      delete window.checkForTournamentPrizes;
    };
  }, []);

  if (!isVisible || !prizeData) {
    return null;
  }

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      setIsVisible(false);
    }, 300);
  };

  const handleClaimPrize = async () => {
    try {
      // Handle prize claiming logic here
      console.log('Claiming prize for tournament:', prizeData.tournamentId);

      // Mark notification as seen/claimed
      await tournamentApiService.markNotificationAsSeen(prizeData.tournamentId);

      // Close the modal
      handleClose();
    } catch (error) {
      console.error('Error claiming prize:', error);
      // You might want to show an error message to the user here
    }
  };

  const handlePlayAgain = () => {
    // Navigate to games or tournament section
    console.log('Navigating to play again');
    handleClose();
  };

  const getPlacementText = (placement: number) => {
    if (placement === 1) return '1st';
    if (placement === 2) return '2nd';
    if (placement === 3) return '3rd';
    return `${placement}th`;
  };

  const getPrizeIcon = (prizeType: string) => {
    switch (prizeType) {
      case 'cash':
        return '💰';
      case 'bonus':
        return '🎁';
      case 'free_spins':
        return '🎰';
      default:
        return '🏆';
    }
  };

  const getPrizeTypeText = (prizeType: string) => {
    switch (prizeType) {
      case 'cash':
        return 'Cash Prize';
      case 'bonus':
        return 'Bonus Credits';
      case 'free_spins':
        return 'Free Spins';
      default:
        return 'Prize';
    }
  };

  return (
    <div className={`tournament-prize-modal-overlay ${isClosing ? 'closing' : ''}`}>
      <div className="tournament-prize-modal">
        {/* Close button */}
        <div className="close-btn" onClick={handleClose}>×</div>

        {/* Tournament Banner */}
        <div className="tournament-banner" style={{ backgroundImage: `url('${prizeData.tournamentBanner}')` }}>
          <div className="banner-overlay">
            <div className="congratulations-badge">🎉 CONGRATULATIONS! 🎉</div>
          </div>
        </div>

        {/* Content */}
        <div className="modal-content">
          {/* Placement Info */}
          <div className="placement-info">
            <h2 className="placement-title">You placed #{prizeData.placement}</h2>
            <p className="placement-subtitle">in {prizeData.tournamentName}</p>
          </div>

          {/* Prize Details */}
          <div className="prize-details">
            <div className="prize-card">
              <div className="prize-icon">{getPrizeIcon(prizeData.prizeType)}</div>
              <div className="prize-info">
                <div className="prize-type">{getPrizeTypeText(prizeData.prizeType)}</div>
                <div className="prize-amount">
                  {prizeData.prizeType === 'free_spins'
                    ? `${prizeData.prizeAmount} Free Spins`
                    : `${prizeData.currency} ${prizeData.prizeAmount.toLocaleString()}`
                  }
                </div>
              </div>
            </div>
          </div>

          {/* Call-to-Action Buttons */}
          <div className="cta-section">
            <button className="cta-button primary" onClick={handleClaimPrize}>
              Claim
            </button>
            {/* <button className="cta-button secondary" onClick={handlePlayAgain}>
              Play Again
            </button> */}
          </div>

          {/* Additional Info */}
          <div className="additional-info">
            <p>Your prize has been added to your account. Keep playing to win more!</p>
          </div>
        </div>
      </div>
    </div>
  );
});